import { useState, useCallback } from "react";

export interface FeatureGuideState {
	/** Whether the tour is currently running. */
	isRunning: boolean;
	/** Current step index. */
	stepIndex: number;
	/** Move to a specific step index. */
	goToStep: (index: number) => void;
	/** Advance to the next step (used internally by FeatureGuide). */
	next: () => void;
	/** Go back one step (used internally by FeatureGuide). */
	prev: () => void;
	/** Start the tour from the beginning. */
	start: () => void;
	/** Stop the tour immediately. */
	stop: () => void;
}

/**
 * Headless hook for controlling a FeatureGuide.
 *
 * Usage:
 * ```tsx
 * const guide = useFeatureGuide();
 * <button onClick={guide.start}>Show Me</button>
 * <FeatureGuide guide={guide} steps={mySteps} />
 * ```
 */
export const useFeatureGuide = (): FeatureGuideState => {
	const [isRunning, setIsRunning] = useState(false);
	const [stepIndex, setStepIndex] = useState(0);

	const start = useCallback(() => {
		setStepIndex(0);
		setIsRunning(true);
	}, []);

	const stop = useCallback(() => {
		setIsRunning(false);
		setStepIndex(0);
	}, []);

	const goToStep = useCallback((index: number) => {
		setStepIndex(index);
	}, []);

	const next = useCallback(() => {
		setStepIndex((prev) => prev + 1);
	}, []);

	const prev = useCallback(() => {
		setStepIndex((prev) => Math.max(0, prev - 1));
	}, []);

	return { isRunning, stepIndex, start, stop, goToStep, next, prev };
};
